import { action, makeObservable, observable, runInAction } from "mobx";
import ErrorStore from "../../common/stores/ErrorStore";
import ProjectsStore from "../../common/stores/ProjectsStore";
import WorkbenchService from "../services/WorkbenchService";
import { WbAction, WbDocument, WbSubject, WbTask } from "../types/WbTypes";
import { TaskService } from "../../tasks/services";

export class WidgetState {
    isLoading: boolean = false;
    hasData: boolean = false;
    error: string | null = null;
    lastQuery: string | null = null;

    constructor() {
        makeObservable(this, {
            isLoading: observable,
            hasData: observable,
            error: observable,
            lastQuery: observable,
        });
    }
}

export default class WorkbenchStore {
    isLoading: boolean = false;
    isInitialLoading: boolean = false;
    error: string | null = null;
    searchInput: string = "";
    isSubjectSearchLoading: boolean = false;
    subjectSearchError: string | null = null;
    subjectSuggestions: WbSubject[] = [];
    subjectSuggestionsTotalCount: number = 0;
    selectedSubject: WbSubject | null = null;

    subjectInfoWidget: WidgetState = new WidgetState();
    tasksWidget: WidgetState = new WidgetState();
    documentsWidget: WidgetState = new WidgetState();
    actionsHistoryWidget: WidgetState = new WidgetState();

    subjectData: WbSubject | null = null;
    tasksData: WbTask[] = [];
    documentsData: WbDocument[] = [];
    actionsHistoryData: WbAction[] = [];

    private subjectSearchDebounceHandle: ReturnType<typeof setTimeout> | null =
        null;
    private subjectSearchRequestId = 0;

    constructor(
        private errorStore: ErrorStore,
        private projectsStore: ProjectsStore,
        private wbService: WorkbenchService,
        private tasksService: TaskService,
    ) {
        makeObservable(this, {
            isLoading: observable,
            isInitialLoading: observable,
            error: observable,
            searchInput: observable,
            isSubjectSearchLoading: observable,
            subjectSearchError: observable,
            subjectSuggestions: observable,
            subjectSuggestionsTotalCount: observable,
            selectedSubject: observable,
            subjectInfoWidget: observable,
            tasksWidget: observable,
            documentsWidget: observable,
            actionsHistoryWidget: observable,
            subjectData: observable,
            tasksData: observable,
            documentsData: observable,
            actionsHistoryData: observable,
            setIsLoading: action,
            setIsInitialLoading: action,
            setError: action,
            setSearchInput: action.bound,
            updateWidgetState: action,
            loadInitialData: action.bound,
            clearSelection: action.bound,
            selectSubjectByValue: action.bound,
            selectSubject: action.bound,
            loadSubjectById: action.bound,
            loadAllWidgetContent: action.bound,
            loadTasksContent: action.bound,
            loadDocumentsContent: action.bound,
            loadActionsHistoryContent: action.bound,
            handleAttachmentDownload: action.bound,
        });
    }

    get projects() {
        return this.projectsStore.projects ?? [];
    }

    getSubjectOptionValue(subject: WbSubject) {
        return `${subject.kind} - ${subject.identifier}`;
    }

    setIsLoading(isLoading: boolean) {
        this.isLoading = isLoading;
    }

    setIsInitialLoading(isInitialLoading: boolean) {
        this.isInitialLoading = isInitialLoading;
    }

    setError(error: string | null) {
        this.error = error;
    }

    setSearchInput(value: string) {
        this.searchInput = value;
        this.subjectSearchError = null;

        if (!value.trim()) {
            this.clearSubjectSearch();
            this.clearSelection();
            return;
        }

        this.scheduleSubjectSearch(value);
    }

    updateWidgetState(
        widgetName:
            | "subjectInfoWidget"
            | "tasksWidget"
            | "documentsWidget"
            | "actionsHistoryWidget",
        updates: Partial<WidgetState>,
    ) {
        const widget = this[widgetName] as WidgetState;
        Object.assign(widget, updates);
    }

    async loadInitialData() {
        this.setIsInitialLoading(true);
        try {
            await this.projectsStore.loadProjects();
        } catch (err) {
            this.setError("Failed to load initial data");
            this.errorStore.addBasicError(err as Error);
        } finally {
            this.setIsInitialLoading(false);
        }
    }

    clearSelection() {
        this.selectedSubject = null;
        this.subjectData = null;
        this.tasksData = [];
        this.documentsData = [];
        this.actionsHistoryData = [];

        this.updateWidgetState("subjectInfoWidget", {
            isLoading: false,
            hasData: false,
            error: null,
            lastQuery: null,
        });
        this.updateWidgetState("tasksWidget", {
            isLoading: false,
            hasData: false,
            error: null,
            lastQuery: null,
        });
        this.updateWidgetState("documentsWidget", {
            isLoading: false,
            hasData: false,
            error: null,
            lastQuery: null,
        });
        this.updateWidgetState("actionsHistoryWidget", {
            isLoading: false,
            hasData: false,
            error: null,
            lastQuery: null,
        });
    }

    async selectSubjectByValue(optionValue: string) {
        const subject =
            this.subjectSuggestions.find(
                (item) => this.getSubjectOptionValue(item) === optionValue,
            ) ??
            (this.selectedSubject &&
            this.getSubjectOptionValue(this.selectedSubject) === optionValue
                ? this.selectedSubject
                : null);

        if (!subject) {
            return;
        }

        await this.selectSubject(subject);
    }

    async selectSubject(subject: WbSubject) {
        const subjectLabel = this.getSubjectOptionValue(subject);

        runInAction(() => {
            this.selectedSubject = subject;
            this.subjectData = subject;
            this.searchInput = subjectLabel;
            this.subjectSuggestions = [];
            this.subjectSuggestionsTotalCount = 0;
            this.subjectSearchError = null;
            this.updateWidgetState("subjectInfoWidget", {
                isLoading: false,
                hasData: true,
                error: null,
                lastQuery: subjectLabel,
            });
        });

        await this.loadAllWidgetContent(subject);
    }

    async loadSubjectById(
        subjectId: string,
        initialSubject?: WbSubject | null,
    ) {
        const initialSubjectMatches = initialSubject?.id === subjectId;

        if (initialSubjectMatches && initialSubject) {
            const subjectLabel = this.getSubjectOptionValue(initialSubject);

            runInAction(() => {
                this.selectedSubject = initialSubject;
                this.subjectData = initialSubject;
                this.searchInput = subjectLabel;
                this.subjectSuggestions = [];
                this.subjectSuggestionsTotalCount = 0;
                this.subjectSearchError = null;
                this.updateWidgetState("subjectInfoWidget", {
                    isLoading: true,
                    hasData: true,
                    error: null,
                    lastQuery: subjectLabel,
                });
            });
        } else {
            runInAction(() => {
                this.clearSelection();
                this.updateWidgetState("subjectInfoWidget", {
                    isLoading: true,
                    hasData: false,
                    error: null,
                    lastQuery: subjectId,
                });
            });
        }

        try {
            const response = await this.wbService.fetchSubject(subjectId);
            let loadedSubject: WbSubject | null = null;

            response
                .map((subject) => {
                    const subjectLabel = this.getSubjectOptionValue(subject);
                    loadedSubject = subject;

                    runInAction(() => {
                        this.selectedSubject = subject;
                        this.subjectData = subject;
                        this.searchInput = subjectLabel;
                        this.subjectSuggestions = [];
                        this.subjectSuggestionsTotalCount = 0;
                        this.subjectSearchError = null;
                        this.updateWidgetState("subjectInfoWidget", {
                            isLoading: false,
                            hasData: true,
                            error: null,
                            lastQuery: subjectLabel,
                        });
                    });
                })
                .mapErr((err) => {
                    runInAction(() => {
                        this.updateWidgetState("subjectInfoWidget", {
                            isLoading: false,
                            hasData: initialSubjectMatches,
                            error: "Failed to load subject",
                        });
                    });
                    this.errorStore.addError(err.data);
                });

            if (loadedSubject) {
                await this.loadAllWidgetContent(loadedSubject);
            }
        } catch (err) {
            runInAction(() => {
                this.updateWidgetState("subjectInfoWidget", {
                    isLoading: false,
                    hasData: initialSubjectMatches,
                    error: "Failed to load subject",
                });
            });
            this.errorStore.addBasicError(err as Error);
        }
    }

    async loadAllWidgetContent(subject: WbSubject) {
        await Promise.all([
            this.loadTasksContent(subject),
            this.loadDocumentsContent(subject),
            this.loadActionsHistoryContent(subject),
        ]);
    }

    async loadTasksContent(subject: WbSubject) {
        const subjectLabel = this.getSubjectOptionValue(subject);
        this.updateWidgetState("tasksWidget", {
            isLoading: true,
            error: null,
            lastQuery: subjectLabel,
        });

        try {
            const [tasksResponse] = await Promise.all([
                this.wbService.fetchSubjectTasks(subject.id),
                this.wait(500),
            ]);

            if (!this.isSubjectSelectionCurrent(subject.id)) {
                return;
            }

            tasksResponse
                .map((data) => {
                    runInAction(() => {
                        if (!this.isSubjectSelectionCurrent(subject.id)) {
                            return;
                        }

                        this.tasksData = data;
                        this.updateWidgetState("tasksWidget", {
                            isLoading: false,
                            hasData: data.length > 0,
                        });
                    });
                })
                .mapErr((err) => {
                    if (!this.isSubjectSelectionCurrent(subject.id)) {
                        return;
                    }

                    this.updateWidgetState("tasksWidget", {
                        isLoading: false,
                        error: "Failed to load tasks",
                    });
                    this.errorStore.addError(err.data);
                });
        } catch (err) {
            if (!this.isSubjectSelectionCurrent(subject.id)) {
                return;
            }

            this.updateWidgetState("tasksWidget", {
                isLoading: false,
                error: "Failed to load tasks",
            });
            this.errorStore.addBasicError(err as Error);
        }
    }

    async loadDocumentsContent(subject: WbSubject) {
        const subjectLabel = this.getSubjectOptionValue(subject);
        this.updateWidgetState("documentsWidget", {
            isLoading: true,
            error: null,
            lastQuery: subjectLabel,
        });

        try {
            const [documentsResponse] = await Promise.all([
                this.wbService.fetchSubjectDocuments(subject.id),
                this.wait(500),
            ]);

            if (!this.isSubjectSelectionCurrent(subject.id)) {
                return;
            }

            documentsResponse
                .map((data) => {
                    runInAction(() => {
                        if (!this.isSubjectSelectionCurrent(subject.id)) {
                            return;
                        }

                        this.documentsData = data;
                        this.updateWidgetState("documentsWidget", {
                            isLoading: false,
                            hasData: data.length > 0,
                        });
                    });
                })
                .mapErr((err) => {
                    if (!this.isSubjectSelectionCurrent(subject.id)) {
                        return;
                    }

                    this.updateWidgetState("documentsWidget", {
                        isLoading: false,
                        error: "Failed to load documents",
                    });
                    this.errorStore.addError(err.data);
                });
        } catch (err) {
            if (!this.isSubjectSelectionCurrent(subject.id)) {
                return;
            }

            this.updateWidgetState("documentsWidget", {
                isLoading: false,
                error: "Failed to load documents",
            });
            this.errorStore.addBasicError(err as Error);
        }
    }

    async loadActionsHistoryContent(subject: WbSubject) {
        const subjectLabel = this.getSubjectOptionValue(subject);
        this.updateWidgetState("actionsHistoryWidget", {
            isLoading: true,
            error: null,
            lastQuery: subjectLabel,
        });

        try {
            const [actionsResponse] = await Promise.all([
                this.wbService.fetchSubjectActions(subject.id),
                this.wait(500),
            ]);

            if (!this.isSubjectSelectionCurrent(subject.id)) {
                return;
            }

            actionsResponse
                .map((data) => {
                    runInAction(() => {
                        if (!this.isSubjectSelectionCurrent(subject.id)) {
                            return;
                        }

                        this.actionsHistoryData = data;
                        this.updateWidgetState("actionsHistoryWidget", {
                            isLoading: false,
                            hasData: data.length > 0,
                        });
                    });
                })
                .mapErr((err) => {
                    if (!this.isSubjectSelectionCurrent(subject.id)) {
                        return;
                    }

                    this.updateWidgetState("actionsHistoryWidget", {
                        isLoading: false,
                        error: "Failed to load actions history",
                    });
                    this.errorStore.addError(err.data);
                });
        } catch (err) {
            if (!this.isSubjectSelectionCurrent(subject.id)) {
                return;
            }

            this.updateWidgetState("actionsHistoryWidget", {
                isLoading: false,
                error: "Failed to load actions history",
            });
            this.errorStore.addBasicError(err as Error);
        }
    }

    async handleAttachmentDownload(packageId: string) {
        try {
            await this.tasksService.handleDownload([packageId]);
        } catch (err) {
            this.errorStore.addBasicError(err);
        }
    }

    wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    private isSubjectSelectionCurrent(subjectId: string) {
        return this.selectedSubject?.id === subjectId;
    }

    private clearSubjectSearch() {
        if (this.subjectSearchDebounceHandle) {
            clearTimeout(this.subjectSearchDebounceHandle);
            this.subjectSearchDebounceHandle = null;
        }

        this.subjectSearchRequestId++;
        this.isSubjectSearchLoading = false;
        this.subjectSearchError = null;
        this.subjectSuggestions = [];
        this.subjectSuggestionsTotalCount = 0;
    }

    private scheduleSubjectSearch(query: string) {
        const trimmedQuery = query.trim();

        if (trimmedQuery.length < 2) {
            this.clearSubjectSearch();
            return;
        }

        if (this.subjectSearchDebounceHandle) {
            clearTimeout(this.subjectSearchDebounceHandle);
        }

        const requestId = ++this.subjectSearchRequestId;
        this.isSubjectSearchLoading = true;

        this.subjectSearchDebounceHandle = setTimeout(() => {
            this.subjectSearchDebounceHandle = null;
            void this.performSubjectSearch(trimmedQuery, requestId);
        }, 300);
    }

    private async performSubjectSearch(query: string, requestId: number) {
        try {
            const response = await this.wbService.searchSubjects(query);

            if (requestId !== this.subjectSearchRequestId) {
                return;
            }

            response
                .map((data) => {
                    runInAction(() => {
                        if (requestId !== this.subjectSearchRequestId) {
                            return;
                        }

                        this.subjectSuggestions = data.items;
                        this.subjectSuggestionsTotalCount = data.totalCount;
                        this.isSubjectSearchLoading = false;
                        this.subjectSearchError = null;
                    });
                })
                .mapErr((err) => {
                    runInAction(() => {
                        if (requestId !== this.subjectSearchRequestId) {
                            return;
                        }

                        this.subjectSuggestions = [];
                        this.subjectSuggestionsTotalCount = 0;
                        this.isSubjectSearchLoading = false;
                        this.subjectSearchError = "Failed to search subjects";
                    });
                    this.errorStore.addError(err.data);
                });
        } catch (err) {
            if (requestId !== this.subjectSearchRequestId) {
                return;
            }

            runInAction(() => {
                this.subjectSuggestions = [];
                this.subjectSuggestionsTotalCount = 0;
                this.isSubjectSearchLoading = false;
                this.subjectSearchError = "Failed to search subjects";
            });
            this.errorStore.addBasicError(err as Error);
        }
    }
}
